/**
 * Base signature builder for EIP-712 message signing
 * Provides core functions for building and signing EIP-712 messages
 * Used by FiClient and other clients that need EIP-712 signing
 */

import { ethers } from "ethers";
import mormSigner from "../../signing/mormSigner";

export interface SignatureBuilderConfig {
  mnemonic: string;
  walletAddress: string;
  chainID?: number;
}

export interface NonceProvider {
  getNextNonce(): Promise<string>;
}

/**
 * Base class for building and signing EIP-712 messages
 */
export class BaseSignatureBuilder {
  protected mnemonic: string;
  protected walletAddress: string;
  protected chainID: number;

  /**
   * Cached private key hex derived from mnemonic.
   *
   * Deriving the key via `ethers.HDNodeWallet.fromPhrase` is CPU-intensive
   * (BIP-39 PBKDF2 + BIP-44 HD derivation, ~10-15 ms per call).  Because the
   * mnemonic and derivation path never change for a given builder instance the
   * result is deterministic, so we derive once and reuse.
   *
   * This eliminates the single biggest per-transaction bottleneck in high-
   * throughput (TPS) scenarios where many signatures are generated concurrently
   * on the JS main thread.
   */
  private cachedPrivateKeyHex: string | null = null;

  constructor(config: SignatureBuilderConfig) {
    this.mnemonic = config.mnemonic;
    this.walletAddress = config.walletAddress;
    this.chainID = config.chainID || 10900;
  }

  /**
   * Generate transaction ID
   * Follows generateTxID pattern from dex_client.go
   */
  generateTxID(): string {
    // Generate unique transaction ID using timestamp and random component
    const timestamp = Date.now();
    const random = Math.floor(Math.random() * 1000000);
    return `tx-${timestamp}-${random}`;
  }

  /**
   * Extract private key from mnemonic in the format expected by WASM.
   * Follows the same pattern as onboard.ts signOnboardMessage.
   *
   * The result is cached after the first derivation because
   * `ethers.HDNodeWallet.fromPhrase` performs BIP-39 PBKDF2 (2 048 rounds of
   * HMAC-SHA-512) + BIP-44 HD derivation, which is CPU-intensive (~10-15 ms).
   * Since the mnemonic and path are immutable for a builder instance, caching
   * is safe and eliminates ~10-15 ms of main-thread blocking per transaction.
   */
  async getPrivateKeyHex(): Promise<string> {
    // Return cached key if available (same mnemonic → same key, always)
    if (this.cachedPrivateKeyHex !== null) {
      return this.cachedPrivateKeyHex;
    }

    // Derive wallet from mnemonic using ethers v6 HDNodeWallet with explicit BIP44 path
    const masterNode = ethers.HDNodeWallet.fromPhrase(
      this.mnemonic.trim(),
      "", // no passphrase
      "m/44'/60'/0'/0/0", // Ethereum standard BIP44 path
    );

    // Validate that derived address matches
    if (masterNode.address.toLowerCase() !== this.walletAddress.toLowerCase()) {
      throw new Error(
        `Address mismatch: derived ${masterNode.address} does not match ${this.walletAddress}`,
      );
    }

    // Extract private key in the exact format Go's crypto.ToECDSA expects
    const privateKeyBigInt = BigInt(masterNode.privateKey);
    const SECP256K1_ORDER = BigInt(
      "0xFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFEBAAEDCE6AF48A03BBFD25E8CD0364141",
    );

    if (privateKeyBigInt === 0n || privateKeyBigInt >= SECP256K1_ORDER) {
      throw new Error(
        `Invalid private key: must be between 1 and secp256k1 order - 1`,
      );
    }

    // Convert BigInt to hex string (matches Go's privKey.D.Bytes() -> hex.EncodeToString)
    const hexWithoutPrefix = privateKeyBigInt.toString(16).toLowerCase();
    const paddedHex = hexWithoutPrefix.padStart(64, "0"); // Pad to 64 hex chars (32 bytes)

    if (!/^[0-9a-f]{64}$/.test(paddedHex)) {
      throw new Error(
        `Invalid private key format: must be 64 lowercase hex characters (0-9, a-f) without 0x prefix`,
      );
    }

    // Cache for subsequent calls
    this.cachedPrivateKeyHex = paddedHex;
    return paddedHex;
  }

  /**
   * Sign payload using generateSingleSignature (same as onboard)
   * This ensures correct recovery ID format matching Go implementation
   */
  async signPayload(payload: Record<string, any>): Promise<string> {
    const privateKeyHex = await this.getPrivateKeyHex();
    const now = Math.floor(Date.now() / 1000);

    // Extract deadline from payload if present
    let deadline: number | undefined;
    if (payload.deadline) {
      deadline = parseInt(payload.deadline, 10);
    }

    // Build EIP712Tx structure for generateSingleSignature
    const eip712Tx: Record<string, any> = {
      txId: "", // Empty txId for signing
      sigType: "ECDSA_LEGACY", // Signature type as string
      threshold: 1,
      timestamp: now,
      payload: payload,
    };

    // Add deadline if present
    if (deadline !== undefined) {
      eip712Tx.deadline = deadline;
    }

    // Use generateSingleSignature to sign the EIP712Tx
    const result = await mormSigner.generateSingleSignature(
      JSON.stringify(eip712Tx),
      privateKeyHex,
      this.walletAddress.toLowerCase(),
    );

    if (!result.success || !result.signature) {
      throw new Error(result.error || "Failed to generate signature");
    }

    return result.signature;
  }

  /**
   * Create complete EIP-712 message with nonce, deadline, owner
   * Follows PlaceOrderMessage pattern from dex_client.go
   * Requires a nonce provider to get the next nonce
   */
  async buildCompleteMessage(
    payload: Record<string, any>,
    nonceProvider: NonceProvider,
    deadlineOffset: number = 3600, // Default: 1 hour from now
  ): Promise<Record<string, any>> {
    const now = Math.floor(Date.now() / 1000); // Unix timestamp in seconds
    const deadline = now + deadlineOffset; // Default: 1 hour from now (in seconds)
    const nonce = await nonceProvider.getNextNonce(); // Get nonce from provider

    return {
      ...payload,
      owner: this.walletAddress,
      nonce: nonce,
      deadline: deadline.toString(), // Unix seconds as string
    };
  }

  /**
   * Build EIP712Tx structure for Router v1 unified write endpoint
   * Follows buildEIP712Tx pattern from dex_client.go
   * Creates the complete transaction structure with txId, sigType, payload, signatures
   */
  buildEIP712Tx(
    payload: Record<string, any>,
    signature: string,
    txId?: string,
  ): Record<string, any> {
    const now = Math.floor(Date.now() / 1000); // Unix timestamp in seconds

    // Extract deadline from payload if present
    let deadline: number | undefined;
    if (payload.deadline) {
      deadline = parseInt(payload.deadline, 10);
    }

    // Build EIP712Tx structure matching WriteTransactionRequest
    const generatedTxId = txId || this.generateTxID();
    const eip712Tx: Record<string, any> = {
      txhash: generatedTxId, // Server requires txhash field (set to same value as txId)
      sigType: 1, // ECDSA_LEGACY
      threshold: 1,
      timestamp: now,
      payload: payload,
      signatures: [
        {
          signer: this.walletAddress,
          signature: signature,
        },
      ],
    };

    // Add deadline if present
    if (deadline !== undefined) {
      eip712Tx.deadline = deadline;
    }

    return eip712Tx;
  }

  /**
   * Get wallet address
   */
  getWalletAddress(): string {
    return this.walletAddress;
  }

  /**
   * Get chain ID
   */
  getChainID(): number {
    return this.chainID;
  }
}
